/**
 * CSRF Protection Utilities
 *
 * Provides Cross-Site Request Forgery protection for the AdapTeach application.
 * Implements token-based CSRF protection with secure token generation and validation.
 */

import { supabase } from "../lib/supabase";

/**
 * CSRF token configuration
 */
const CSRF_CONFIG = {
  TOKEN_LENGTH: 32,
  TOKEN_EXPIRY: 60 * 60 * 1000, // 1 hour in milliseconds
  STORAGE_KEY: "adapteach_csrf_token",
  HEADER_NAME: "X-CSRF-Token",
  COOKIE_NAME: "csrf_token",
} as const;

/**
 * Interface for CSRF token data
 */
interface CSRFTokenData {
  token: string;
  timestamp: number;
  userId?: string;
}

/**
 * Generate a cryptographically secure random token
 */
const generateSecureToken = (): string => {
  // Use crypto.getRandomValues for secure random generation
  const array = new Uint8Array(CSRF_CONFIG.TOKEN_LENGTH);
  crypto.getRandomValues(array);

  // Convert to base64url (URL-safe base64)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

/**
 * Get current user ID from Supabase session
 */
const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user?.id || null;
  } catch (error) {
    console.error("Error getting current user ID:", error);
    return null;
  }
};

/**
 * Store CSRF token securely in sessionStorage
 */
const storeToken = (tokenData: CSRFTokenData): void => {
  try {
    sessionStorage.setItem(CSRF_CONFIG.STORAGE_KEY, JSON.stringify(tokenData));
  } catch (error) {
    console.error("Error storing CSRF token:", error);
  }
};

/**
 * Retrieve CSRF token from sessionStorage
 */
const retrieveToken = (): CSRFTokenData | null => {
  try {
    const stored = sessionStorage.getItem(CSRF_CONFIG.STORAGE_KEY);
    if (!stored) return null;

    const tokenData: CSRFTokenData = JSON.parse(stored);

    // Check if token has expired
    if (Date.now() - tokenData.timestamp > CSRF_CONFIG.TOKEN_EXPIRY) {
      sessionStorage.removeItem(CSRF_CONFIG.STORAGE_KEY);
      return null;
    }

    return tokenData;
  } catch (error) {
    console.error("Error retrieving CSRF token:", error);
    sessionStorage.removeItem(CSRF_CONFIG.STORAGE_KEY);
    return null;
  }
};

/**
 * Generate a new CSRF token
 */
export const generateCSRFToken = async (): Promise<string> => {
  const token = generateSecureToken();
  const userId = await getCurrentUserId();

  const tokenData: CSRFTokenData = {
    token,
    timestamp: Date.now(),
    userId: userId || undefined,
  };

  storeToken(tokenData);

  return token;
};

/**
 * Get current CSRF token (generate new one if needed)
 */
export const getCSRFToken = async (): Promise<string> => {
  const existingToken = retrieveToken();

  if (existingToken) {
    // Verify the token is still valid for the current user
    const currentUserId = await getCurrentUserId();
    if (existingToken.userId === currentUserId) {
      return existingToken.token;
    }
  }

  // Generate new token if none exists or user changed
  return generateCSRFToken();
};

/**
 * Validate CSRF token
 */
export const validateCSRFToken = async (
  providedToken: string
): Promise<boolean> => {
  if (!providedToken || typeof providedToken !== "string") {
    console.warn("🚨 CSRF validation failed: No token provided");
    return false;
  }

  const storedTokenData = retrieveToken();

  if (!storedTokenData) {
    console.warn("🚨 CSRF validation failed: No stored token found");
    return false;
  }

  // Check if tokens match
  if (storedTokenData.token !== providedToken) {
    console.warn("🚨 CSRF validation failed: Token mismatch");
    return false;
  }

  // Verify user context
  const currentUserId = await getCurrentUserId();
  if (storedTokenData.userId !== currentUserId) {
    console.warn("🚨 CSRF validation failed: User context mismatch");
    return false;
  }

  return true;
};

/**
 * Clear CSRF token (e.g., on logout)
 */
export const clearCSRFToken = (): void => {
  try {
    sessionStorage.removeItem(CSRF_CONFIG.STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing CSRF token:", error);
  }
};

/**
 * Add CSRF token to request headers
 */
export const addCSRFHeader = async (
  headers: Record<string, string> = {}
): Promise<Record<string, string>> => {
  const token = await getCSRFToken();

  return {
    ...headers,
    [CSRF_CONFIG.HEADER_NAME]: token,
  };
};

/**
 * Create CSRF-protected fetch wrapper
 */
export const csrfFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Only add CSRF protection for state-changing methods
  const method = options.method?.toUpperCase() || "GET";
  const needsCSRF = ["POST", "PUT", "PATCH", "DELETE"].includes(method);

  if (needsCSRF) {
    const headers = await addCSRFHeader(
      (options.headers as Record<string, string>) || {}
    );
    options.headers = headers;
  }

  return fetch(url, options);
};

/**
 * CSRF protection middleware for form submissions
 */
export const withCSRFProtection = async <T extends Record<string, unknown>>(
  formData: T,
  submitFunction: (data: T, csrfToken: string) => Promise<unknown>
): Promise<unknown> => {
  try {
    const csrfToken = await getCSRFToken();

    // Add CSRF token to form data
    const protectedData = {
      ...formData,
      _csrf: csrfToken,
    };

    return await submitFunction(protectedData, csrfToken);
  } catch (error) {
    console.error("🚨 CSRF protection error:", error);
    throw new Error(
      "Security validation failed. Please refresh the page and try again."
    );
  }
};

/**
 * Initialize CSRF protection (call on app startup)
 */
export const initializeCSRFProtection = async (): Promise<void> => {
  try {
    // Generate initial token
    await generateCSRFToken();

    // Note: Auth state change listener is now handled in securityInit.ts
    // to avoid duplicate listeners and conflicts
  } catch (error) {
    console.error("Error initializing CSRF protection:", error);
    throw error; // Re-throw to let caller handle
  }
};

/**
 * Validate CSRF token from request (for server-side validation)
 */
export const validateCSRFFromRequest = (
  headers: Record<string, string>,
  body?: Record<string, unknown>
): boolean => {
  // Check header first
  const headerToken =
    headers[CSRF_CONFIG.HEADER_NAME] ||
    headers[CSRF_CONFIG.HEADER_NAME.toLowerCase()];

  // Check body as fallback
  const bodyToken = body?._csrf;

  const providedToken = headerToken || bodyToken;

  if (!providedToken) {
    console.warn("🚨 CSRF validation failed: No token in request");
    return false;
  }

  // Note: This is a simplified validation for client-side use
  // Server-side validation should be more comprehensive
  return typeof providedToken === "string" && providedToken.length > 0;
};

/**
 * Create CSRF token input for forms
 */
export const createCSRFInput = async (): Promise<string> => {
  const token = await getCSRFToken();
  return `<input type="hidden" name="_csrf" value="${token}" />`;
};
